import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import { useLang } from "../i18n/LanguageContext";
import { Seo } from "../components/Seo";
import { PageHero } from "../components/PageHero";
import { Stagger, itemVariant } from "../components/Reveal";
import { getKnowledgeArticles, getKnowledgeCategories } from "../data/knowledge";

const DOMAIN = "https://www.plasto-nord.com";

export default function Knowledge() {
  const { t, lang } = useLang();
  const articles = getKnowledgeArticles(lang);
  const categories = getKnowledgeCategories(lang);

  const itemList = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Wissens-Center TPU – PlastoNord",
    itemListElement: articles.map((a, i) => ({
      "@type": "ListItem", position: i + 1, name: a.title, url: DOMAIN + `/wissen/${a.slug}`,
    })),
  };

  return (
    <>
      <Seo
        title={t("kn.seoTitle")}
        description={t("kn.seoDesc")}
        path="/wissen"
        breadcrumbs={[{ name: t("kn.home"), path: "/" }, { name: t("nav.knowledge"), path: "/wissen" }]}
        schema={itemList}
      />
      <PageHero
        overline={t("nav.knowledge")}
        title={t("kn.hubTitle")}
        subtitle={t("kn.hubSubtitle")}
      />

      <section className="py-16 md:py-24">
        <div className="pn-container space-y-16">
          {categories.map((cat) => {
            const items = articles.filter((a) => a.category === cat.key);
            if (!items.length) return null;
            return (
              <div key={cat.key} data-testid={`knowledge-cat-${cat.key}`}>
                <div className="flex items-center gap-4">
                  <h2 className="font-head text-2xl font-semibold tracking-tight text-[#16181D] md:text-3xl">{cat.label}</h2>
                  <span className="h-px flex-1 bg-[#e4e9f0]" />
                  <span className="font-mono text-xs text-[#9aa0a8]">{items.length}</span>
                </div>
                <Stagger className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {items.map((a) => (
                    <motion.div key={a.slug} variants={itemVariant}>
                      <Link to={`/wissen/${a.slug}`} data-testid={`knowledge-card-${a.slug}`} className="pn-card group flex h-full flex-col p-7">
                        <span className="font-mono text-xs text-[#E2611C]">{a.date}</span>
                        <h3 className="mt-3 font-head text-lg font-semibold leading-snug text-[#16181D] group-hover:text-[#E2611C]">{a.title}</h3>
                        <p className="mt-3 flex-1 text-sm leading-relaxed text-[#4a4d52]">{a.summary.length > 160 ? a.summary.slice(0, 157) + "…" : a.summary}</p>
                        <span className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-[#16181D]">{t("cta.more")} <ArrowUpRight size={15} /></span>
                      </Link>
                    </motion.div>
                  ))}
                </Stagger>
              </div>
            );
          })}
        </div>
      </section>
    </>
  );
}
